'use client';

import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import LottieView from '@/utils/lottie-helper';
import type { LottieRef } from '@/utils/lottie-helper';
import { getCurrency } from '@/utils/currency';
import styles from '../index.module.css';
import { useTranslations } from 'next-intl';
import getAnimationData from '../lottie/get.json';
import getLineAnimationData from '../lottie/get-line.json';

interface GetAnimationProps {
  /** 是否显示动画 */
  visible: boolean;
  /** 余额差值（增加的金额） */
  balanceDiff: number | string;
  /** 货币类型 */
  currency?: string;
  /** 动画完成回调 */
  onComplete?: () => void;
  /** 触发 CountUp 回调（在 4s 时调用） */
  onTriggerCountUp?: () => void;
}

export default function GetAnimation({
  visible,
  balanceDiff,
  currency = 'INR',
  onComplete,
  onTriggerCountUp,
}: GetAnimationProps) {
  const [showMask, setShowMask] = useState(false);
  const [preloaded, setPreloaded] = useState(false);
  const maskOffTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countUpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lottieLoaded, setLottieLoaded] = useState(false);
  const backgroundLottieRef = useRef<LottieRef['current']>(null);
  const lineLottieRef = useRef<LottieRef['current']>(null);
  const t_gullak = useTranslations('gullak');

  const clearTimers = () => {
    if (maskOffTimeoutRef.current) {
      clearTimeout(maskOffTimeoutRef.current);
      maskOffTimeoutRef.current = null;
    }
    if (countUpTimeoutRef.current) {
      clearTimeout(countUpTimeoutRef.current);
      countUpTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (!visible) {
      setShowMask(false);
      clearTimers();
      lineLottieRef.current?.stop();
      backgroundLottieRef.current?.stop();
      return;
    }
    if (!lottieLoaded) return;
    backgroundLottieRef.current?.goToAndPlay(0, true);
    lineLottieRef.current?.goToAndPlay(0, true);
  }, [visible, lottieLoaded]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const handleDOMLoaded = () => {
    setLottieLoaded(true);
    // 显示黑色蒙层
    setShowMask(true);

    clearTimers();
    // 在 2.3 秒时移除蒙层
    maskOffTimeoutRef.current = setTimeout(() => {
      setShowMask(false);
    }, 2300);

    // 在 4 秒时触发 CountUp
    countUpTimeoutRef.current = setTimeout(() => {
      onTriggerCountUp?.();
    }, 4000);
  };

  const handleComplete = () => {
    clearTimers();
    setShowMask(false);
    onComplete?.();
  };

  return (
    <>
      {/* 预加载：visible=false 时挂载 Lottie，但不展示任何蒙层/全屏容器 */}
      {!visible && !preloaded && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: -9999,
            top: -9999,
            width: 1,
            height: 1,
            overflow: 'hidden',
            opacity: 0,
            pointerEvents: 'none',
          }}
        >
          <LottieView
            animationData={getAnimationData}
            loop={false}
            autoplay={false}
            onDOMLoaded={() => setPreloaded(true)}
            renderer="canvas"
          />
          <LottieView
            animationData={getLineAnimationData}
            loop={false}
            autoplay={false}
            renderer="canvas"
          />
        </div>
      )}

      {/* 使用 Radix Dialog 管理 Portal 挂载，确保覆盖在 Drawer 之上 */}
      <Dialog.Root open={visible}>
        <Dialog.Portal>
          <Dialog.Content
            className="lottie-gullak-get fixed inset-0 z-[9998] outline-none"
            onEscapeKeyDown={e => e.preventDefault()}
            onInteractOutside={e => e.preventDefault()}
            onPointerDownOutside={e => e.preventDefault()}
            aria-describedby={undefined}
          >
            <VisuallyHidden.Root>
              <Dialog.Title>Get Animation</Dialog.Title>
            </VisuallyHidden.Root>

            {/* 黑色蒙层 - 80% 透明度 */}
            {showMask && (
              <div className="absolute inset-0 bg-black bg-opacity-80" />
            )}

            {/* 背景动画 */}
            <LottieView
              animationData={getAnimationData}
              loop={false}
              autoplay={true}
              lottieRef={backgroundLottieRef}
              className="absolute inset-0 z-10"
              style={{ width: '100%', height: '100%' }}
              onDOMLoaded={handleDOMLoaded}
              onComplete={handleComplete}
              renderer="canvas"
            />

            {/* 文字和线条动画容器 */}
            {lottieLoaded && (
              <div className="absolute inset-x-0 z-20 h-[26vw] top-[56vh]">
                <div className="absolute left-0 right-0 top-0 w-[100%] h-[100%] flex flex-col justify-center items-center">
                  {/* 线条动画 */}
                  <LottieView
                    animationData={getLineAnimationData}
                    loop={false}
                    autoplay={true}
                    lottieRef={lineLottieRef}
                    className={`absolute left-0 right-0 top-0 ${styles['lottie-get-line-animation']}`}
                    style={{ width: '100%', height: '100%' }}
                    renderer="canvas"
                  />

                  {/* 金额文字 */}
                  <div className={`text-[#fff] font-medium text-center ${styles['lottie-cash-animation']}`}>
                    <div className="font-bold text-[26px] leading-7 w-50">
                      {balanceDiff === '<0.01' ? balanceDiff.replace('<', `<${getCurrency(currency)}`) : `${getCurrency(currency)}${balanceDiff}`}
                    </div>
                    <div className="leading-4 text-[rgba(255,255,255,0.5)] text-ss">
                      {t_gullak('gullakCash')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
